<template>
  <div id="app" class="d-flex flex-column min-vh-100">
    <!-- Lien d'évitement -->
    <a href="#main-content" class="skip-to-content">Aller au contenu principal</a>

    <!-- Header -->
    <header>
      <nav
        class="navbar navbar-expand-lg fixed-top glass-navbar transition-all"
        :class="{ 'navbar-scrolled py-2': isScrolled, 'py-3': !isScrolled }"
        itemscope
        itemtype="https://schema.org/SiteNavigationElement"
      >
        <div class="container">
          <router-link
            to="/"
            class="navbar-brand d-flex align-items-center gap-3"
            aria-label="Retour à l'accueil du Restaurant Pizzeria Treviglio"
            title="Accueil Pizzeria Treviglio"
          >
            <img
              src="@/assets/logo2.png"
              alt="Logo Restaurant Pizzeria Treviglio Flawinne Namur"
              class="logo"
            >
            <!-- AJOUT DU NOM DU RESTAURANT ICI -->
            <span class="brand-text d-none d-sm-block">
              Pizzeria Treviglio
            </span>
          </router-link>

          <button
            class="navbar-toggler custom-toggler"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarNavDropdown"
            aria-controls="navbarNavDropdown"
            aria-expanded="false"
            aria-label="Ouvrir le menu de navigation"
          >
            <span class="navbar-toggler-icon"></span>
          </button>

          <div class="collapse navbar-collapse" id="navbarNavDropdown">
            <ul class="navbar-nav ms-auto mb-2 mb-lg-0 align-items-lg-center gap-2">
              <li class="nav-item" itemprop="name">
                <router-link
                  to="/"
                  exact
                  class="nav-link modern-nav-link"
                  @click="closeMenu"
                  itemprop="url"
                  title="Accueil de notre pizzeria à Flawinne"
                >
                  <i class="fas fa-home me-1"></i> Accueil
                </router-link>
              </li>

              <li class="nav-item" itemprop="name">
                <router-link
                  to="/menu"
                  class="nav-link modern-nav-link"
                  @click="closeMenu"
                  itemprop="url"
                  title="Découvrez notre carte de pizzas et plats italiens"
                >
                  <i class="fas fa-utensils me-1"></i> Notre Carte
                </router-link>
              </li>

              <li class="nav-item" itemprop="name">
                <router-link
                  to="/about"
                  class="nav-link modern-nav-link"
                  @click="closeMenu"
                  itemprop="url"
                  title="L'histoire de notre restaurant italien"
                >
                  <i class="fas fa-info-circle me-1"></i> À propos
                </router-link>
              </li>

              <li class="nav-item" itemprop="name">
                <router-link
                  to="/contact"
                  class="nav-link modern-nav-link"
                  @click="closeMenu"
                  itemprop="url"
                  title="Contactez-nous ou réservez votre table"
                >
                  <i class="fas fa-envelope me-1"></i> Contact
                </router-link>
              </li>

              <li class="nav-item ms-lg-3 mt-3 mt-lg-0">
                <a
                  href="tel:+3281570790"
                  class="btn btn-premium-primary rounded-pill px-4 py-2 shadow-sm pulse-btn w-100 w-lg-auto"
                  aria-label="Appeler pour commander au +32 81 57 07 90"
                >
                  <i class="fas fa-phone-alt me-2"></i> Commander
                </a>
              </li>
            </ul>
          </div>
        </div>
      </nav>
    </header>

    <!-- Contenu -->
    <main id="main-content" class="main-content flex-grow-1">
      <router-view v-slot="{ Component }">
        <transition name="page-fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </main>

    <!-- Back to top -->
    <transition name="fade">
      <button
        v-show="showBackToTop"
        @click="scrollToTop"
        class="btn btn-premium-top shadow-lg"
        aria-label="Remonter en haut de la page"
      >
        <i class="fas fa-arrow-up icon-centered"></i>
      </button>
    </transition>

    <!-- Cookies (RGPD) -->
    <CookieConsent />

    <!-- Footer -->
    <footer class="modern-footer mt-auto" itemscope itemtype="https://schema.org/Restaurant">
      <meta itemprop="name" content="Pizzeria Treviglio">
      <meta itemprop="url" content="https://pizzeriatreviglio.be">
      <meta itemprop="servesCuisine" content="Italienne, Pizza">
      <meta itemprop="priceRange" content="€€">
      <meta itemprop="hasMenu" content="https://pizzeriatreviglio.be/menu">

      <!-- CTA haut du footer -->
      <div class="footer-top-band">
        <div class="container">
          <div class="footer-cta-card">
            <div class="row align-items-center g-4">
              <div class="col-lg-7 text-center text-lg-start">
                <p class="footer-eyebrow mb-2">Pizzeria Treviglio · Flawinne</p>
                <h3 class="footer-cta-title mb-2">Réserver ou commander en quelques secondes</h3>
                <p class="footer-cta-text mb-0">
                  Une envie de pizza artisanale, un plat italien ou une réservation de table ? Contactez-nous directement.
                </p>
              </div>

              <div class="col-lg-5">
                <div class="footer-cta-actions">
                  <a href="tel:+3281570790" class="btn footer-cta-btn footer-cta-primary">
                    <i class="fas fa-phone-alt me-2"></i> Appeler
                  </a>

                  <router-link
                    to="/contact"
                    class="btn footer-cta-btn footer-cta-secondary"
                    @click="closeMenu"
                  >
                    <i class="fas fa-envelope me-2"></i> Nous écrire
                  </router-link>

                  <button
                    class="btn footer-cta-btn footer-cta-outline"
                    @click="openGoogleMaps"
                    type="button"
                  >
                    <i class="fas fa-map-marked-alt me-2"></i> Itinéraire
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Bloc principal -->
      <div class="container footer-main py-5">
        <div class="row g-4 g-xl-5">
          <!-- Marque -->
          <div class="col-lg-4 col-md-6">
            <div class="footer-brand-block">
              <h4 class="footer-title mb-3">Pizzeria Treviglio</h4>

              <p itemprop="description" class="footer-text footer-brand-text mb-4">
                Votre restaurant italien à Flawinne, avec pizzas artisanales, cuisine authentique et ambiance conviviale près de Namur.
              </p>

              <!-- Contacts avec grille stricte -->
              <div
                itemprop="address"
                itemscope
                itemtype="https://schema.org/PostalAddress"
                class="footer-contact-grid"
              >
                <!-- Ligne 1 : Adresse -->
                <div class="footer-contact-row">
                  <div class="footer-icon-wrapper">
                    <span class="footer-contact-icon">
                      <i class="fas fa-map-marker-alt"></i>
                    </span>
                  </div>
                  <div class="footer-content-wrapper">
                    <span itemprop="streetAddress">Rue Georges Emmanuel 3</span><br>
                    <span itemprop="postalCode">5020</span> <span itemprop="addressLocality">Flawinne (Namur)</span>, <span itemprop="addressCountry">Belgique</span>
                  </div>
                </div>

                <!-- Ligne 2 : Téléphone -->
                <div class="footer-contact-row">
                  <div class="footer-icon-wrapper">
                    <span class="footer-contact-icon">
                      <i class="fas fa-phone-alt"></i>
                    </span>
                  </div>
                  <div class="footer-content-wrapper">
                    <a href="tel:+3281570790" class="footer-link" itemprop="telephone">+32 81 57 07 90</a>
                    <span class="footer-separator mx-2">·</span>
                    <a href="tel:+32493762927" class="footer-link">+32 493 76 29 27</a>
                  </div>
                </div>

                <!-- Ligne 3 : Email -->
                <div class="footer-contact-row">
                  <div class="footer-icon-wrapper">
                    <span class="footer-contact-icon">
                      <i class="fas fa-envelope"></i>
                    </span>
                  </div>
                  <div class="footer-content-wrapper">
                    <a href="mailto:info@pizzeriatreviglio.be" class="footer-link" itemprop="email">
                      info@pizzeriatreviglio.be
                    </a>
                  </div>
                </div>

                <!-- Ligne 4 : TVA -->
                <div class="footer-contact-row">
                  <div class="footer-icon-wrapper">
                    <span class="footer-contact-icon">
                      <i class="fas fa-building"></i>
                    </span>
                  </div>
                  <div class="footer-content-wrapper">
                    <span itemprop="taxID">TVA BE 0781.581.854</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Navigation -->
          <div class="col-lg-3 col-md-6">
            <div class="footer-block">
              <h4 class="footer-title">Navigation</h4>
              <ul class="footer-nav-list list-unstyled mb-0">
                <li>
                  <router-link to="/" class="footer-nav-link" @click="closeMenu">
                    <i class="fas fa-angle-right"></i>
                    <span>Accueil</span>
                  </router-link>
                </li>
                <li>
                  <router-link to="/menu" class="footer-nav-link" @click="closeMenu">
                    <i class="fas fa-angle-right"></i>
                    <span>Notre Carte</span>
                  </router-link>
                </li>
                <li>
                  <router-link to="/about" class="footer-nav-link" @click="closeMenu">
                    <i class="fas fa-angle-right"></i>
                    <span>À propos</span>
                  </router-link>
                </li>
                <li>
                  <router-link to="/contact" class="footer-nav-link" @click="closeMenu">
                    <i class="fas fa-angle-right"></i>
                    <span>Contact & Réservation</span>
                  </router-link>
                </li>
              </ul>
            </div>
          </div>

          <!-- Services / Social -->
          <div class="col-lg-5 col-md-12">
            <div class="footer-block">
              <h4 class="footer-title">Infos utiles</h4>

              <div class="footer-badges">
                <span class="footer-badge">
                  <i class="fas fa-pizza-slice"></i> Pizzas artisanales
                </span>
                <span class="footer-badge">
                  <i class="fas fa-fire"></i> Four à bois
                </span>
                <span class="footer-badge">
                  <i class="fas fa-store"></i> Sur place & à emporter
                </span>
                <span class="footer-badge">
                  <i class="fas fa-motorcycle"></i> Livraison rapide
                </span>
              </div>

              <div class="footer-social-wrapper mt-4">
                <p class="footer-social-text mb-3">
                  Suivez-nous et partagez votre expérience.
                </p>

                <div class="social-icons d-flex align-items-center flex-wrap gap-3">
                  <a
                    href="https://www.facebook.com/profile.php?id=100088617854588"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="social-icon"
                    aria-label="Notre page Facebook"
                    itemprop="sameAs"
                  >
                    <i class="fab fa-facebook-f"></i>
                  </a>

                  <a
                    href="https://www.instagram.com/trevigliopizzeria/"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="social-icon"
                    aria-label="Notre page Instagram"
                    itemprop="sameAs"
                  >
                    <i class="fab fa-instagram"></i>
                  </a>

                  <div
                    class="fb-share-button d-inline-block m-0"
                    data-href="https://pizzeriatreviglio.be"
                    data-layout="button"
                    data-size="large"
                  ></div>
                </div>
              </div>

              <!-- Liens RGPD -->
              <div class="footer-legal mt-4 pt-4">
                <router-link :to="{ name: 'PrivacyPolicyPage' }" class="footer-legal-link">
                  Politique de Confidentialité
                </router-link>
                <router-link :to="{ name: 'LegalNoticePage' }" class="footer-legal-link">
                  Mentions Légales
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Bas footer -->
      <div class="footer-bottom">
        <div class="container">
          <div class="footer-bottom-inner">
            <p class="mb-0 small text-muted">
              &copy; {{ currentYear }} TREVIGLIO SRL. Tous droits réservés.
            </p>
            <p class="mb-0 small text-muted">
              Conçu avec <span class="text-danger">&hearts;</span> par
              <a
                href="https://cyberglow.be"
                target="_blank"
                rel="noopener noreferrer"
                class="cyber-link"
              >
                CyberGlow
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
// Import du composant RGPD
import CookieConsent from './components/CookieConsent.vue';
import { Collapse } from 'bootstrap';

export default {
  name: 'App',
  components: {
    CookieConsent,
  },
  data() {
    return {
      currentYear: new Date().getFullYear(),
      isScrolled: false,
      showBackToTop: false,
    };
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll, { passive: true });
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    handleScroll() {
      this.isScrolled = window.scrollY > 50;
      this.showBackToTop = window.scrollY > 400;
    },
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    closeMenu() {
      const navbarCollapse = document.getElementById('navbarNavDropdown');
      if (navbarCollapse && navbarCollapse.classList.contains('show')) {
        const bsCollapse = Collapse.getInstance(navbarCollapse);
        if (bsCollapse) {
          bsCollapse.hide();
        } else {
          new Collapse(navbarCollapse).hide();
        }
      }
    },
    openGoogleMaps() {
      const lat = 50.45969;
      const lng = 4.84142;
      const url = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
      window.open(url, '_blank');
    },
  },
};
</script>

<style scoped>
@import './assets/custom-styles.css';

/* ==========================================================================
   Mise en page & Accessibilité
   ========================================================================== */
#app {
  font-family: 'Quattrocento Sans', 'Montserrat', sans-serif;
  color: #212121;
  overflow-x: hidden;
}

.main-content {
  padding-top: 90px;
}

.skip-to-content {
  position: absolute;
  top: -40px;
  left: 0;
  background: #d32f2f;
  color: #fff;
  padding: 10px 15px;
  z-index: 10000;
  transition: top 0.3s ease;
  text-decoration: none;
  font-weight: bold;
  border-bottom-right-radius: 8px;
}

.skip-to-content:focus {
  top: 0;
}

/* ==========================================================================
   Transitions
   ========================================================================== */
.transition-all {
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.page-fade-enter-active,
.page-fade-leave-active {
  transition: opacity 0.35s ease, transform 0.35s ease;
}

.page-fade-enter-from {
  opacity: 0;
  transform: translateY(15px);
}

.page-fade-leave-to {
  opacity: 0;
  transform: translateY(-15px);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(15px);
}

/* ==========================================================================
   Header & Navbar Premium
   ========================================================================== */
.glass-navbar {
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.6);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.03);
}

.navbar-scrolled {
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  border-bottom: 1px solid rgba(211, 47, 47, 0.1);
}

.navbar-brand {
  text-decoration: none !important;
}

.navbar-brand .logo {
  height: 60px;
  width: auto;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.brand-text {
  font-family: 'Merriweather', serif;
  font-size: 1.4rem;
  font-weight: 800;
  color: #222;
  letter-spacing: -0.5px;
  transition: color 0.3s ease;
}

.navbar-scrolled .navbar-brand .logo {
  height: 48px;
}

.navbar-scrolled .brand-text {
  font-size: 1.25rem;
}

.navbar-brand:hover .logo {
  transform: scale(1.06) rotate(-1deg);
}

.navbar-brand:hover .brand-text {
  color: #d32f2f;
}

.modern-nav-link {
  font-family: 'Montserrat', sans-serif;
  font-size: 0.95rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #222 !important;
  transition: all 0.3s ease;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  position: relative;
  overflow: hidden;
}

.modern-nav-link:hover,
.modern-nav-link.router-link-active {
  color: #d32f2f !important;
  background-color: rgba(211, 47, 47, 0.05);
}

.modern-nav-link.router-link-active::after {
  content: '';
  position: absolute;
  bottom: 4px;
  left: 50%;
  transform: translateX(-50%);
  width: 25px;
  height: 3px;
  background-color: #d32f2f;
  border-radius: 2px;
  transition: width 0.3s ease;
}

.modern-nav-link:hover::after {
  width: 35px;
}

.btn-premium-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: 'Montserrat', sans-serif;
  background-color: #d32f2f;
  color: #fff;
  border: none;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  transition: all 0.3s ease;
  position: relative;
  font-size: 0.9rem;
  text-decoration: none;
}

.btn-premium-primary:hover {
  background-color: #a62626;
  color: #fff;
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(211, 47, 47, 0.3) !important;
}

@keyframes pulse {
  0% { box-shadow: 0 0 0 0 rgba(211, 47, 47, 0.5); }
  70% { box-shadow: 0 0 0 12px rgba(211, 47, 47, 0); }
  100% { box-shadow: 0 0 0 0 rgba(211, 47, 47, 0); }
}

.pulse-btn {
  animation: pulse 2.5s infinite;
}

.pulse-btn:hover {
  animation: none;
}

.custom-toggler {
  border: 2px solid rgba(211, 47, 47, 0.2);
  border-radius: 10px;
  transition: all 0.3s ease;
}

.custom-toggler:focus,
.custom-toggler:active {
  box-shadow: 0 0 0 4px rgba(211, 47, 47, 0.15);
  outline: none;
}

.custom-toggler .navbar-toggler-icon {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 30 30'%3e%3cpath stroke='rgba%28211, 47, 47, 1%29' stroke-linecap='round' stroke-miterlimit='10' stroke-width='2.5' d='M4 7h22M4 15h22M4 23h22'/%3e%3c/svg%3e");
}

@media (max-width: 991px) {
  .navbar-collapse {
    background: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(15px);
    border-radius: 16px;
    padding: 1.5rem;
    margin-top: 1rem;
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.8);
  }

  .navbar-nav {
    text-align: center;
  }

  .modern-nav-link {
    font-size: 1.1rem;
    padding: 0.8rem;
  }
}

/* ==========================================================================
   Back to top
   ========================================================================== */
.btn-premium-top {
  position: fixed;
  bottom: 40px;
  right: 40px;
  width: 55px;
  height: 55px;
  border-radius: 50%;
  background: linear-gradient(135deg, #d32f2f, #a62626);
  color: #fff;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  font-size: 1.3rem;
  padding: 0;
  line-height: 0;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.icon-centered {
  display: block;
  margin: 0;
  padding: 0;
  transform: translateY(-1px);
}

.btn-premium-top:hover {
  transform: translateY(-6px) scale(1.05);
  box-shadow: 0 12px 25px rgba(211, 47, 47, 0.4) !important;
}

@media (max-width: 768px) {
  .btn-premium-top {
    bottom: 25px;
    right: 25px;
    width: 48px;
    height: 48px;
    font-size: 1.1rem;
  }
}

/* ==========================================================================
   Footer Premium
   ========================================================================== */
.modern-footer {
  background:
    radial-gradient(circle at top left, rgba(211, 47, 47, 0.10), transparent 28%),
    radial-gradient(circle at top right, rgba(255, 255, 255, 0.05), transparent 18%),
    linear-gradient(180deg, #181818 0%, #0d0d0d 100%);
  color: #f8f9fa;
  font-family: 'Montserrat', sans-serif;
  position: relative;
  overflow: hidden;
}

.modern-footer::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #d32f2f, #a62626, #d32f2f);
}

/* Bande CTA */
.footer-top-band {
  padding: 3rem 0 0;
}

.footer-cta-card {
  background: linear-gradient(135deg, rgba(211, 47, 47, 0.15), rgba(255, 255, 255, 0.03));
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 28px;
  padding: 2.5rem;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.22);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
}

.footer-eyebrow {
  font-size: 0.82rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: #ffb3b3;
  font-weight: 700;
}

.footer-cta-title {
  font-family: 'Merriweather', serif;
  font-size: 2.2rem;
  font-weight: 800;
  color: #fff;
  margin: 0;
}

.footer-cta-text {
  color: #d7d7d7;
  line-height: 1.7;
  max-width: 650px;
}

.footer-cta-actions {
  display: grid;
  gap: 1rem;
}

.footer-cta-btn {
  border-radius: 999px;
  padding: 1rem 1.4rem;
  font-weight: 700;
  text-decoration: none;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.footer-cta-primary {
  background: #d32f2f;
  color: #fff;
  border: none;
  box-shadow: 0 8px 20px rgba(211, 47, 47, 0.2);
}

.footer-cta-primary:hover {
  background: #b92626;
  color: #fff;
  transform: translateY(-2px);
  box-shadow: 0 12px 25px rgba(211, 47, 47, 0.3);
}

.footer-cta-secondary {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.footer-cta-secondary:hover {
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  transform: translateY(-2px);
}

.footer-cta-outline {
  background: transparent;
  color: #ffd9d9;
  border: 1px solid rgba(255, 179, 179, 0.35);
}

.footer-cta-outline:hover {
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  transform: translateY(-2px);
}

/* Contenu principal footer */
.footer-main {
  position: relative;
  z-index: 1;
}

.footer-brand-block,
.footer-block {
  height: 100%;
}

.footer-brand-text {
  max-width: 420px;
}

.footer-title {
  font-family: 'Merriweather', serif;
  color: #ffffff;
  font-size: 1.4rem;
  font-weight: 700;
  position: relative;
  display: inline-block;
  margin-bottom: 1.5rem;
  padding-bottom: 12px;
  letter-spacing: 0.4px;
}

.footer-title::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: 0;
  width: 50px;
  height: 3px;
  border-radius: 999px;
  background: linear-gradient(90deg, #d32f2f, transparent);
}

.footer-text {
  color: #bfc3c8;
  font-size: 0.98rem;
  line-height: 1.8;
}

/* ==========================================================================
   Contacts (GRILLE STRICTE ET ALIGNÉE)
   ========================================================================== */
.footer-contact-grid {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  margin-top: 1.5rem;
}

.footer-contact-row {
  display: grid;
  grid-template-columns: 48px 1fr;
  gap: 1rem;
  align-items: center;
}

.footer-icon-wrapper {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.footer-contact-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #d32f2f, #a62626);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 20px rgba(211, 47, 47, 0.20);
  flex-shrink: 0;
}

.footer-contact-icon i {
  margin: 0;
  padding: 0;
  line-height: 1;
  font-size: 1.1rem;
}

.footer-content-wrapper {
  color: #d4d7db;
  line-height: 1.55;
  word-break: break-word;
  font-size: 0.95rem;
}

.footer-content-wrapper br {
  display: block;
  content: "";
  margin-top: 3px;
}

/* ==========================================================================
   Liens Footer
   ========================================================================== */
.footer-link {
  color: #d4d7db;
  text-decoration: none;
  transition: color 0.25s ease;
  display: inline;
}

.footer-link:hover {
  color: #ffffff;
}

.footer-separator {
  color: rgba(255, 255, 255, 0.28);
}

.footer-nav-list {
  display: grid;
  gap: 0.75rem;
}

.footer-nav-link {
  display: inline-flex;
  align-items: center;
  gap: 0.7rem;
  color: #d4d7db;
  text-decoration: none;
  transition: all 0.25s ease;
  padding: 0.2rem 0;
}

.footer-nav-link i {
  color: #ff7a7a;
  font-size: 0.85rem;
  line-height: 1;
}

.footer-nav-link:hover {
  color: #fff;
  transform: translateX(4px);
}

/* ==========================================================================
   Badges
   ========================================================================== */
.footer-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
}

.footer-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.7rem 1rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.05);
  color: #f2f2f2;
  font-size: 0.92rem;
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.footer-badge i {
  color: #ff8f8f;
}

/* ==========================================================================
   Social
   ========================================================================== */
.footer-social-text {
  color: #bfc3c8;
}

.social-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  border-radius: 50%;
  font-size: 1.2rem;
  transition: all 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  text-decoration: none;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.social-icon i {
  margin: 0 !important;
  padding: 0 !important;
  line-height: 1 !important;
}

.social-icon:hover {
  background-color: #d32f2f;
  color: #fff;
  transform: translateY(-4px);
  box-shadow: 0 8px 22px rgba(211, 47, 47, 0.35);
  border-color: transparent;
}

/* ==========================================================================
   Légal
   ========================================================================== */
.footer-legal {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.footer-legal-link {
  color: #cfd3d7;
  text-decoration: none;
  font-size: 0.94rem;
  transition: color 0.25s ease;
}

.footer-legal-link:hover {
  color: #fff;
}

/* ==========================================================================
   Bas footer
   ========================================================================== */
.footer-bottom {
  background-color: rgba(0, 0, 0, 0.4);
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  padding: 1.2rem 0;
}

.footer-bottom-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.cyber-link {
  color: #d4d7db;
  text-decoration: none;
  font-weight: 700;
  transition: color 0.3s ease, text-shadow 0.3s ease;
}

.cyber-link:hover {
  color: #05c8f1;
  text-shadow: 0 0 8px rgba(5, 200, 241, 0.35);
}

/* ==========================================================================
   Responsive
   ========================================================================== */
@media (max-width: 991px) {
  .footer-cta-title {
    font-size: 1.8rem;
  }

  .footer-cta-card {
    padding: 1.8rem;
  }

  .footer-bottom-inner {
    flex-direction: column;
    text-align: center;
  }
}

@media (max-width: 767px) {
  .footer-top-band {
    padding-top: 2rem;
  }

  .footer-cta-title {
    font-size: 1.5rem;
  }

  .footer-title::after {
    left: 50%;
    transform: translateX(-50%);
  }

  .footer-brand-block,
  .footer-block {
    text-align: center;
  }

  .footer-brand-text {
    margin-left: auto;
    margin-right: auto;
  }

  .footer-contact-row {
    justify-content: flex-start;
  }

  .footer-badges,
  .social-icons,
  .footer-legal {
    justify-content: center;
  }

  .footer-cta-actions {
    grid-template-columns: 1fr;
  }
}
</style>